import AutocompleteInput, { Place } from '@/components/AutocompleteInput/AutocompleteInput';
import { Column, Row } from '@/components/SettingsLayout';
import { Text, Divider, Input, SwitchInput } from '@/ui';
import { Country, State } from 'country-state-city';
import getSymbolFromCurrency from 'currency-symbol-map';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styles from '../styles.module.css';
import { CustomSelect } from '@/components';
import { NUMBER_WITH_DECIMATES_VALIDATION_PATTERN, RATE_VALIDATION_PATTERN } from '@/constants';
import { industriesDropDown } from '@/pages/accounts/components/CreateOrgForm';
import { OrgFilter, useGetAllOrganizationQuery } from '@/store/slices/organizationsSlice';
import { CompanyTypes } from '@/types';
import { useGetUser } from '@/hooks';
import { usePaginationFilter } from '@/hooks/usePaginationFilter';
import { skipToken } from '@reduxjs/toolkit/query';
import { Plan } from '@/services/Plans/plans.service';
import { useGetPlansQuery } from '@/store';

type Props = {
  showManageOrganizations?: boolean;
  mode?: 'create' | 'edit';
  plans?: Plan[];
};
export const CompanyFormFields = ({
  showManageOrganizations = false,
  mode = 'create',
  plans,
}: Props) => {
  const { t } = useTranslation();
  const { control, register, watch, setValue, getValues, resetField } = useFormContext();
  const { currentUser, company } = useGetUser();
  const [showPassword, setShowPassword] = useState(false);
  const { filter } = usePaginationFilter<OrgFilter>({
    search: '',
    limit: 100,
    offset: 0,
    type: CompanyTypes.ORGANIZATION,
    organizationId: currentUser.companyId,
  });
  const { data } = useGetAllOrganizationQuery(
    showManageOrganizations && company?._id
      ? {
          id: company._id,
          filter,
        }
      : skipToken,
  );
  const organization = watch('organization');
  const { data: selectedOrganizationPlans } = useGetPlansQuery(
    !plans && organization?.value
      ? {
          organizationId: organization?.value,
        }
      : skipToken,
  );
  const selectedCountry = watch('country');
  const address = watch('address');
  const shippingAddress = watch('shipping.address');
  const sameAsShipping = watch('same_as_billing');

  useEffect(() => {
    if (!selectedOrganizationPlans) {
      return;
    }
    setValue('plan', {
      value: selectedOrganizationPlans?.[0]._id,
      label: selectedOrganizationPlans?.[0].name,
    });
  }, [selectedOrganizationPlans]);

  useEffect(() => {
    if (selectedCountry) {
      const currency = Country.getCountryByCode(selectedCountry.value)?.currency;
      if (!currency) {
        setValue('currency', '');
        return;
      }
      const symbol = getSymbolFromCurrency(currency);
      const currencyString = `${currency} (${symbol})`;
      setValue('currency', currencyString);
    }
  }, [selectedCountry, setValue]);

  useEffect(() => {
    if (sameAsShipping) {
      const address = getValues('address');
      const city = getValues('city');
      const postCode = getValues('postCode');
      const country = getValues('country');
      setValue('shipping.address', address);
      setValue('shipping.city', city);
      setValue('shipping.postCode', postCode);
      setValue('shipping.country', country);
    } else {
      // Revert to default values for each shipping field
      resetField('shipping.address');
      resetField('shipping.city');
      resetField('shipping.postCode');
      resetField('shipping.country');
    }
  }, [sameAsShipping, getValues, setValue, resetField]);

  const handlePlaceSelect = (place: Place, mode: 'billing' | 'shipping' = 'billing') => {
    const formatted_address = place.formatted_address;
    if (mode === 'billing') {
      setValue('address', formatted_address);
    } else {
      setValue('shipping.address', formatted_address);
    }

    if (!place.address_components) {
      return;
    }

    const cityComponent = place.address_components.find(
      (component) =>
        component.types.includes('locality') ||
        component.types.includes('administrative_area_level_1') ||
        component.types.includes('administrative_area_level_2') ||
        component.types.includes('administrative_area_level_3'),
    );
    if (cityComponent) {
      setValue(mode === 'billing' ? 'city' : 'shipping.city', cityComponent.long_name);
    }
    const postalCodeComponent = place.address_components.find((component) =>
      component.types.includes('postal_code'),
    );
    if (postalCodeComponent) {
      setValue(
        mode === 'billing' ? 'postCode' : 'shipping.postCode',
        postalCodeComponent.long_name,
      );
    }
    const stateComponent = place.address_components.find((component) =>
      component.types.includes('administrative_area_level_1'),
    );
    if (stateComponent && selectedCountry?.value) {
      const newState = State.getStateByCodeAndCountry(
        stateComponent.short_name,
        selectedCountry.value,
      );
      if (newState) {
        setValue(mode === 'billing' ? 'state' : 'shipping.state', {
          value: newState.isoCode,
          label: newState.name,
        });
      }
    }
    const countryComponent = place.address_components.find((component) =>
      component.types.includes('country'),
    );
    if (countryComponent) {
      const countryValue = countryComponent.short_name;
      const countryLabel = countryComponent.long_name;
      setValue(mode === 'billing' ? 'country' : 'shipping.country', {
        value: countryValue,
        label: countryLabel,
      });
    }
  };

  const organizationOptions = useMemo(() => {
    if (data && data.data.length > 0) {
      return data?.data.map((org) => ({
        label: org.name,
        value: org._id,
      }));
    } else {
      return [];
    }
  }, [data]);

  return (
    <Column gapSize='20' fullWidth>
      {showManageOrganizations && (
        <Row gapSize='24' align='start' fullWidth justify='justify-between'>
          <Column gapSize='4'>
            <Text size='s' weight='semi-bold'>
              {t('Companies.managing_organization')}
            </Text>
            <Text size='xs' weight='light-medium'>
              {t('Companies.managing_organization_text')}
            </Text>
          </Column>
          <div className={styles.smallSelect}>
            <Controller
              name='organization'
              control={control}
              rules={{ required: t('Form.required.organization') }}
              defaultValue={organizationOptions[0]}
              render={({ field }) => (
                <CustomSelect
                  options={organizationOptions}
                  defValue={organizationOptions[0]}
                  field={field}
                />
              )}
            />
          </div>
        </Row>
      )}
      <Column gapSize='12' fullWidth>
        <Column gapSize='4'>
          <Text size='s' weight='semi-bold'>
            {t('Companies.owners_details')}
          </Text>
          <Text size='xs' weight='light-medium'>
            {t('Companies.owners_details_text')}
          </Text>
        </Column>
        <Row fullWidth justify='justify-between' align='start'>
          <label htmlFor='first_name'>
            <Text size='s' weight='light-medium'>
              {t('Companies.full_name')}
            </Text>
          </label>
          <Row gapSize='12' fullWidth={false}>
            <div className={styles.smallInput}>
              <Input
                id='first_name'
                placeholder={t('Form.placeholders.name')}
                {...register('first_name', {
                  required: t('Form.required.name'),
                })}
              />
            </div>
            <div className={styles.smallInput}>
              <Input
                id='last_name'
                placeholder={t('Form.placeholders.lastName')}
                {...register('last_name', {
                  required: t('Form.required.lastName'),
                })}
              />
            </div>
          </Row>
        </Row>
        <Row fullWidth justify='justify-between' align='start'>
          <label htmlFor='email'>
            <Text size='s' weight='light-medium'>
              {t('Form.email')}
            </Text>
          </label>
          <div className={styles.bigInput}>
            <Input
              id='email'
              placeholder={t('Form.placeholders.email')}
              variant='full'
              disabled
              {...register('email', {
                required: t('Form.required.email'),
                pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              })}
            />
          </div>
        </Row>
        {mode === 'create' && (
          <Row fullWidth justify='justify-between' align='start'>
            <label htmlFor='password'>
              <Text size='s' weight='light-medium'>
                {t('Form.password')}
              </Text>
            </label>
            <Column fullWidth={false} gapSize='4'>
              <div className={styles.bigInput}>
                <Input
                  id='password'
                  type={!showPassword ? 'password' : 'text'}
                  placeholder={t('Form.placeholders.password')}
                  variant='full'
                  {...register('password')}
                />
              </div>
              <Row gapSize='12' fullWidth justify='justify-between'>
                <div></div>
                <button
                  type='button'
                  onClick={() => setShowPassword(!showPassword)}
                  className='text-primary text-xs font-normal cursor-pointer bg-transparent border-none'
                >
                  <Text size='xs' weight='light-medium' color='blue'>
                    {t('Shared.show')}
                  </Text>
                </button>
              </Row>
            </Column>
          </Row>
        )}
        <Row fullWidth justify='justify-between' align='start'>
          <label htmlFor='phone'>
            <Text size='s' weight='light-medium'>
              {t('Form.phone')}
            </Text>
          </label>
          <div className={styles.bigInput}>
            <Input
              id='phone'
              placeholder={t('Form.placeholders.phone')}
              variant='full'
              {...register('phone')}
            />
          </div>
        </Row>
      </Column>
      <Divider />
      <Column gapSize='12' fullWidth>
        <Column gapSize='4' fullWidth>
          <Text size='s' weight='semi-bold'>
            {t('Companies.company_details')}
          </Text>
          <Text size='xs' weight='light-medium'>
            {t('Companies.company_details_text')}
          </Text>
        </Column>
        <Row fullWidth justify='justify-between' align='start'>
          <label htmlFor='company_name'>
            <Text size='s' weight='light-medium'>
              {t('Form.company_name')}
            </Text>
          </label>
          <div className={styles.bigInput}>
            <Input
              id={'company_name'}
              placeholder={t('Form.placeholders.company_name')}
              variant='full'
              {...register('company_name', {
                required: t('Form.required.company_name'),
              })}
            />
          </div>
        </Row>
        <Row fullWidth justify='justify-between' align='start'>
          <label htmlFor='industry'>
            <Text size='s' weight='light-medium'>
              {t('Accounts.industry')}
            </Text>
          </label>
          <div className={styles.bigInput}>
            <Controller
              name={'industry'}
              control={control}
              rules={{ required: t('Form.required.industry') }}
              defaultValue={{
                value: industriesDropDown[0].value,
                label: t(`Accounts.${industriesDropDown[0].label}`),
              }}
              render={({ field }) => {
                const options = industriesDropDown.map((industry) => ({
                  value: industry.value,
                  label: t(`Accounts.${industry.label}`),
                }));
                return (
                  <CustomSelect
                    options={options}
                    variant={'full'}
                    isSearchable={true}
                    field={field}
                    inputId='industry'
                    defValue={options[0]}
                  />
                );
              }}
            />
          </div>
        </Row>
        <Row fullWidth justify='justify-between' align='start'>
          <label htmlFor='country'>
            <Text size='s' weight='light-medium'>
              {t('Shared.country')}
            </Text>
          </label>
          <Row gapSize='12' fullWidth={false}>
            <div className={styles.smallInput}>
              <Controller
                name={'country'}
                control={control}
                rules={{ required: t('Form.required.country') }}
                render={({ field }) => {
                  const options = Country.getAllCountries().map((country) => ({
                    value: country.isoCode,
                    label: country.name,
                  }));

                  return (
                    <CustomSelect
                      options={options}
                      variant={'full'}
                      isSearchable={true}
                      field={field}
                      inputId='country'
                    />
                  );
                }}
              />
            </div>
            <div className={styles.smallInput}>
              <Input id='currency' {...register('currency')} disabled placeholder='US($)-dollar' />
            </div>
          </Row>
        </Row>
        <Row fullWidth justify='justify-between' align='start'>
          <label htmlFor='address'>
            <Text size='s' weight='light-medium'>
              {t('Accounts.billing_address')}
            </Text>
          </label>
          <div className={styles.bigInput}>
            <AutocompleteInput
              onChange={(place) => handlePlaceSelect(place, 'billing')}
              placeholder={t('Form.placeholders.address')}
              value={address as string}
              defaultValue={address}
            />
          </div>
        </Row>
        <Row fullWidth justify='justify-between' align='start'>
          <div />
          <div className={styles.bigInput}>
            <Input
              id='address2'
              variant='full'
              placeholder={`${t('Form.address')} 2`}
              {...register('address2')}
            />
          </div>
        </Row>
        <Row fullWidth justify='justify-between' align='start'>
          <div />
          <Row gapSize='12' fullWidth={false}>
            <div className={styles.smallInput}>
              <Input
                id='city'
                variant='full'
                placeholder={t('Form.placeholders.city')}
                {...register('city', { required: t('Form.required.city') })}
              />
            </div>
            <div className={styles.smallInput}>
              <Input
                variant='full'
                placeholder={t('Form.postal_code')}
                {...register('postCode', {
                  required: t('Form.required.postal_code'),
                })}
              />
            </div>
          </Row>
        </Row>
      </Column>
      {/* <Divider /> */}
      <Column gapSize='12' fullWidth>
        <Row fullWidth justify='justify-between'>
          <Row fullWidth>
            <Text size='s' weight='light-medium'>
              {t('Companies.shipping_address')}
            </Text>
          </Row>
          <Row fullWidth justify='justify-between'>
            <Text size='s' weight='light-medium'>
              {t('Companies.shipping_address_same_as_billing')}
            </Text>
            <Controller
              name={'same_as_billing'}
              control={control}
              defaultValue={false}
              render={({ field }) => (
                <>
                  <SwitchInput field={field} disabled={false} />
                </>
              )}
            />
          </Row>
        </Row>
        <Row fullWidth justify='justify-between' align='start'>
          <div></div>
          <div className={styles.bigInput}>
            <AutocompleteInput
              onChange={(place) => handlePlaceSelect(place, 'shipping')}
              placeholder={t('Form.placeholders.address')}
              value={shippingAddress as string}
              defaultValue={shippingAddress}
            />
          </div>
        </Row>
        <Row fullWidth justify='justify-between' align='start'>
          <div />
          <div className={styles.bigInput}>
            <Input
              variant='full'
              placeholder={`${t('Form.address')} 2`}
              {...register('shipping.address2', {})}
            />
          </div>
        </Row>
        <Row fullWidth justify='justify-between' align='start'>
          <div />
          <Row gapSize='12' fullWidth={false}>
            <div className={styles.smallInput}>
              <Input
                id='shipping.address'
                variant='full'
                placeholder={t('Form.placeholders.city')}
                {...register('shipping.city', {
                  required: t('Form.required.city'),
                })}
              />
            </div>
            <div className={styles.smallInput}>
              <Input
                id='shipping.address'
                variant='full'
                placeholder={t('Form.postal_code')}
                {...register('shipping.postCode', {
                  required: t('Form.required.postal_code'),
                })}
              />
            </div>
          </Row>
        </Row>
      </Column>
      <Divider />
      {mode === 'create' && (
        <>
          <Column gapSize='12' fullWidth>
            <Text size='s' weight='semi-bold'>
              {t('Companies.tax_setup')}
            </Text>
            <Row fullWidth justify='justify-between' align='start'>
              <label htmlFor='country'>
                <Text size='s' weight='light-medium' color='base-blue'>
                  {t('Settings.tax_options')}
                </Text>
              </label>
              <div className={styles.bigInput}>
                <Controller
                  control={control}
                  name='isTaxInclusive'
                  rules={{ required: t('Form.required.taxOption') }}
                  render={({ field }) => {
                    const taxOptions = [
                      { label: t('Form.tax_inclusive'), value: 'inclusive' },
                      { label: t('Form.tax_exclusive'), value: 'exclusive' },
                    ];
                    return (
                      <CustomSelect
                        id='isTaxInclusive'
                        variant='full'
                        options={taxOptions}
                        field={field}
                      />
                    );
                  }}
                />
              </div>
            </Row>
            <Row fullWidth justify='justify-between' align='start'>
              <label htmlFor='default_tax'>
                <Text size='s' weight='light-medium'>
                  {t('Companies.default_tax')}
                </Text>
              </label>
              <Row gapSize='12' fullWidth={false}>
                <div className={styles.smallInput}>
                  <Input
                    id='default_tax'
                    placeholder={t('Form.placeholders.taxName')}
                    variant='full'
                    {...register('tax_name')}
                  />
                </div>
                <div className={styles.smallInput}>
                  <div className={styles.inputWithIcon}>
                    <Input
                      id='rate'
                      text='center'
                      placeholder={t('Boarding.tax_rate_placeholder')}
                      onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                        if (!RATE_VALIDATION_PATTERN.test(event.key)) {
                          event.preventDefault();
                        }
                      }}
                      {...register('rate', {
                        pattern: {
                          value: NUMBER_WITH_DECIMATES_VALIDATION_PATTERN,
                          message: t('Form.errors.rate'),
                        },
                        validate: (value) =>
                          parseFloat(value) <= 100 ? true : t('Form.errors.rate_less_than_100'),
                        required: t('Form.errors.rate_less_than_100'),
                        onBlur: (event) => {
                          const value = event.target.value;
                          if (value) {
                            const formattedValue = parseFloat(value).toFixed(2);
                            event.target.value = formattedValue;
                            event.target.dispatchEvent(
                              new Event('input', {
                                bubbles: true,
                                cancelable: true,
                              }),
                            );
                          }
                        },
                      })}
                    />
                  </div>
                </div>
              </Row>
            </Row>
          </Column>
          <Divider />
        </>
      )}
      <Column gapSize='12' fullWidth>
        <Text size='s' weight='semi-bold'>
          {t('Companies.plan_setup')}
        </Text>
        <Row fullWidth justify='justify-between' align='start'>
          <label htmlFor='country'>
            <div className={styles.permissionSwitchContainer}>
              <Text size='s' weight='light-medium' color='base-blue'>
                {t('Companies.select_plan')}
              </Text>
            </div>
          </label>
          {((plans && plans.length > 0) ||
            (selectedOrganizationPlans && selectedOrganizationPlans.length > 0)) && (
            <div className={styles.bigInput}>
              <Controller
                control={control}
                name='plan'
                render={({ field }) => {
                  const currentPlans = plans || selectedOrganizationPlans || [];
                  const options = currentPlans.map((plan) => ({
                    label: plan.name,
                    value: plan._id,
                  }));

                  return (
                    <CustomSelect
                      id='plan'
                      variant='full'
                      options={options}
                      field={field}
                      menuPosition='fixed'
                    />
                  );
                }}
              />
            </div>
          )}
        </Row>
      </Column>
    </Column>
  );
};
